// src/pages/payments/PaymentDetailPage/PaymentDetailPage.jsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import PaymentsAPI from '../../../api/v3/payments-api-v3';

const BASE = '/app/sales/bagspa';
const API_BASE = process.env.REACT_APP_API_BASE;

const METHOD_OPTIONS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'virtual_account', label: 'Virtual Account' },
  { value: 'debit_card', label: 'EDC (Debit)' },
  { value: 'visa', label: 'EDC (VISA)' },
  { value: 'mastercard', label: 'EDC (Mastercard)' },
  { value: 'amex', label: 'EDC (Amex)' },
  { value: 'others', label: 'Others' },
];

function formatIDR(num) { return 'Rp ' + Number(num || 0).toLocaleString('id-ID'); }
function isPdfUrl(url=''){ return /\.pdf(\?|$)/i.test(url); }

function StatusBadge({ status }) {
  if (!status) return null;
  const map = {
    pending: 'secondary',
    awaiting_review: 'warning',
    complete_dp: 'success',
    complete_full: 'success',
    failed: 'danger',
    canceled: 'dark',
  };
  const cls = map[status] || 'secondary';
  return <span className={`badge bg-${cls}`}>{status}</span>;
}

/**
 * Single uploader panel that can target DP or Full.
 * Shows the receipts gallery of the selected target payment.
 */
function SingleReceiptsPanel({
  target,            // 'dp' | 'full'
  onTargetChange,    // (val) => void
  dp, full,
  busy,
  onAfterUpload,     // (updatedPayment) => void
  onSetPrimary,      // (paymentId, receiptId) => void
  onDelete,          // (paymentId, receiptId) => void
}) {
  const payment = target === 'full' ? full : dp;

  const [files, setFiles] = useState([]);
  const [note, setNote]   = useState('');
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  // Auto-fallback if the current target becomes unavailable
  useEffect(() => {
    if (target === 'full' && !full && dp) onTargetChange('dp');
    if (target === 'dp'   && !dp   && full) onTargetChange('full');
  }, [dp, full, target, onTargetChange]);

  const previews = useMemo(
    () => files.map(f => ({
      name: f.name,
      type: f.type,
      url: URL.createObjectURL(f),
      isPdf: /pdf$/i.test(f.type) || /\.pdf$/i.test(f.name),
      size: f.size,
    })),
    [files]
  );

  useEffect(() => {
    return () => previews.forEach(p => URL.revokeObjectURL(p.url));
  }, [previews]);

  function handleFiles(listLike) {
    const list = Array.from(listLike || []);
    if (!list.length) return;
    setFiles(prev => [...prev, ...list]);
  }

  async function doUpload() {
    if (!payment) return alert('Belum ada payment target (DP/Full).');
    if (!files.length) return alert('Pilih minimal 1 file terlebih dahulu.');
    const res = await PaymentsAPI.uploadReceipts(payment.id, files, note);
    setFiles([]);
    setNote('');
    onAfterUpload?.(res.payment);
  }

  return (
    <div className="card mb-4">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h5 className="mb-0">Receipts</h5>
          <div className="d-flex align-items-center" style={{gap:8}}>
            <label className="mb-0 small">Target:</label>
            <select
              className="form-select"
              value={target}
              onChange={(e) => onTargetChange(e.target.value)}
            >
              <option value="dp"   disabled={!dp}>Down Payment{!dp ? ' (N/A)' : ''}</option>
              <option value="full" disabled={!full}>Full Payment{!full ? ' (N/A)' : ''}</option>
            </select>
          </div>
        </div>

        {/* Target receipts gallery */}
        {payment?.receipts?.length > 0 && (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <h6 className="mb-2">Uploaded Receipts</h6>
              <small className="text-muted">Primary receipt is highlighted.</small>
            </div>
            <div className="d-flex flex-wrap gap-3 mb-3">
              {payment.receipts.map(r => (
                <div key={r.id} className="position-relative" style={{width: 140}}>
                  <div className={`border rounded ${r.primary ? 'border-success' : 'border-secondary'}`} style={{padding:8}}>
                    {isPdfUrl(r.url)
                      ? <div className="text-center" style={{height:100, display:'grid', placeItems:'center'}}>📄 PDF</div>
                      : <img src={r.url} alt="receipt" style={{width:'100%', height:100, objectFit:'cover', borderRadius:6}} />
                    }
                    <div className="mt-2 small text-truncate">
                      <a href={r.url} target="_blank" rel="noreferrer">{r.url}</a>
                    </div>
                    <div className="d-flex gap-2 mt-2">
                      {!r.primary && (
                        <button
                          className="btn btn-sm btn-outline-success"
                          disabled={busy}
                          onClick={() => onSetPrimary(payment.id, r.id)}
                        >Set Primary</button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-danger"
                        disabled={busy}
                        onClick={() => onDelete(payment.id, r.id)}
                      >Delete</button>
                    </div>
                  </div>
                  {r.primary && (
                    <span className="badge bg-success position-absolute" style={{top:-8,right:-8}}>
                      PRIMARY
                    </span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* One dropzone for all (multi-file) */}
        <div
          className={`p-4 border rounded ${dragging ? 'border-primary bg-light' : 'border-secondary'}`}
          onDragOver={(e)=>{e.preventDefault(); setDragging(true);}}
          onDragLeave={(e)=>{e.preventDefault(); setDragging(false);}}
          onDrop={(e)=>{e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files);}}
          role="button"
          onClick={() => inputRef.current?.click()}
          title="Click or drag files here"
          style={{cursor:'pointer'}}
        >
          <div className="text-center">
            <div className="mb-2" style={{fontSize: 28}}>⬆️</div>
            <div className="fw-bold">Drag & drop files here</div>
            <div className="text-muted">or click to browse (images / PDF)</div>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="image/*,application/pdf"
              hidden
              onChange={(e)=>handleFiles(e.target.files)}
            />
          </div>
        </div>

        {previews.length > 0 && (
          <div className="mt-3">
            <div className="d-flex align-items-center justify-content-between">
              <h6 className="mb-2">Pending Upload</h6>
              <small className="text-muted">Remove any file before uploading.</small>
            </div>
            <div className="d-flex flex-wrap gap-3">
              {previews.map((p, idx) => (
                <div key={idx} className="border rounded p-2" style={{width:140}}>
                  {p.isPdf
                    ? <div className="text-center" style={{height:100, display:'grid', placeItems:'center'}}>📄 PDF</div>
                    : <img src={p.url} alt={p.name} style={{width:'100%', height:100, objectFit:'cover', borderRadius:6}}/>
                  }
                  <div className="small text-truncate mt-1" title={p.name}>{p.name}</div>
                  <button
                    className="btn btn-sm btn-outline-secondary mt-1 w-100"
                    onClick={()=>setFiles(prev=>prev.filter((_,i)=>i!==idx))}
                  >Remove</button>
                </div>
              ))}
            </div>

            <div className="row g-2 mt-2">
              <div className="col-md-9">
                <input
                  className="form-control"
                  placeholder="Note (optional), e.g., DP transfer, FINAL EDC"
                  value={note}
                  onChange={e=>setNote(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <button className="btn btn-primary w-100" disabled={busy} onClick={doUpload}>
                  {busy ? 'Uploading…' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PaymentDetailPage() {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');

  // We show both payments on this page
  const [orderInfo, setOrderInfo] = useState(null);
  const [dp,   setDp]   = useState(null);
  const [full, setFull] = useState(null);

  // Single uploader target
  const [target, setTarget] = useState('full');
  const isFinalPaid = useMemo(() => full?.status === 'complete_full', [full]);

  const outstanding = useMemo(() => {
    const fin = Number(orderInfo?.order_summary?.final_fixed_price || 0);
    const adv = Number(orderInfo?.order_summary?.advance_payment_price || 0);
    return Math.max(0, fin - adv);
  }, [orderInfo]);

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  async function loadAll() {
    try {
      setLoading(true);
      setError('');

      // 1) Load the payment we navigated to
      const res = await PaymentsAPI.show(id);
      const current = res?.item || res;

      setOrderInfo(current);

      // 2) Fetch all payments for this order
      const list = await PaymentsAPI.list({ order_id: current.order_id });
      const items = list?.items || [];

      // 3) Fetch receipts for each payment via show (so we have .receipts)
      const detailed = await Promise.all(items.map(it => PaymentsAPI.show(it.id).then(j => j.item || j)));

      // 4) Partition DP vs FULL
      const dpPay   = detailed.find(p => p.payment_type === 'down_payment') || null;
      const fullPay = detailed.find(p => p.payment_type === 'full_payment') || null;
      setDp(dpPay);
      setFull(fullPay);

      // 5) Default target selection
      setTarget(fullPay ? 'full' : (dpPay ? 'dp' : 'full'));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function changeMethod(payment, method) {
    try {
      setBusy(true);
      const res = await PaymentsAPI.update(payment.id, { payment_method: method });
      const updated = res.item || res;
      if (updated.payment_type === 'down_payment') setDp(updated);
      if (updated.payment_type === 'full_payment') setFull(updated);
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  async function approve(payment, newStatus) {
    const c = orderInfo?.customer || {};
    const summary = [
      `Order: ${orderInfo?.order_no}`,
      `Customer: ${c.name || '-'}`,
      `Type: ${payment.payment_type}`,
      `Method: ${payment.payment_method}`,
      `Amount: ${formatIDR(payment.amount)}`,
      '',
      'Confirm approve/reject?'
    ].join('\n');
    if (!window.confirm(summary)) return;

    try {
      setBusy(true);
      const res = await PaymentsAPI.update(payment.id, { status: newStatus });
      const updated = res.item || res;
      if (updated.payment_type === 'down_payment') setDp(updated);
      if (updated.payment_type === 'full_payment') setFull(updated);
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  async function onApproveFullClick() {
    const fin = Number(orderInfo?.order_summary?.final_fixed_price || 0);

    if (fin <= 0) {
      alert('Pembayaran belum bisa Full, karena belum ada harga FINAL dari vendor (mis. Kaya Leather). ' +
            'Silakan update Total Final Price di Order Detail.');
      return;
    }
    if (!full) {
      alert('Belum ada FULL payment yang dapat di-approve. Placeholder FULL dibuat saat Order di-accept.');
      return;
    }
    await approve(full, 'complete_full');
  }

  async function setPrimary(paymentId, receiptId) {
    try {
      setBusy(true);
      const data = await PaymentsAPI.setPrimaryReceipt(paymentId, receiptId);
      const updated = data.payment;
      if (updated.payment_type === 'down_payment') setDp(updated);
      if (updated.payment_type === 'full_payment') setFull(updated);
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  async function deleteReceipt(paymentId, receiptId) {
    if (!window.confirm('Delete this receipt?')) return;
    try {
      setBusy(true);
      const data = await PaymentsAPI.deleteReceipt(paymentId, receiptId);
      const updated = data.payment;
      if (updated.payment_type === 'down_payment') setDp(updated);
      if (updated.payment_type === 'full_payment') setFull(updated);
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  if (loading) return <div>Loading…</div>;
  if (error)   return <div className="alert alert-danger">{error}</div>;

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Payments for Order {orderInfo?.order_no}</h3>
        <div className="btn-group">
          {orderInfo?.order_id && (
            <a
              className="btn btn-outline-dark"
              href={`${API_BASE}/api/v3/orders/${orderInfo.order_id}/tanda_masuk_invoice`}
              target="_blank" rel="noreferrer"
              title="Invoice PDF (page 2). Outstanding = Final - DP"
            >
              Invoice (PDF)
            </a>
          )}
          <Link to={`${BASE}/payments`} className="btn btn-light">&larr; Back</Link>
        </div>
      </div>

      {/* Quick summary */}
      <div className={`alert ${isFinalPaid ? 'alert-success' : 'alert-secondary'} small`}>
        <div style={isFinalPaid ? { textDecoration: 'line-through' } : undefined}>
          Outstanding (Final − DP): <b>{formatIDR(outstanding)}</b>
        </div>
        {isFinalPaid && (
          <div className="mt-1">
            <span className="badge bg-success">PAID</span>
          </div>
        )}
      </div>

      {/* One shared uploader */}
      <SingleReceiptsPanel
        target={target}
        onTargetChange={setTarget}
        dp={dp}
        full={full}
        busy={busy}
        onAfterUpload={(updated) => {
          if (updated.payment_type === 'down_payment') setDp(updated);
          if (updated.payment_type === 'full_payment') setFull(updated);
        }}
        onSetPrimary={setPrimary}
        onDelete={deleteReceipt}
      />

      {/* DP card */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Down Payment (DP)</h5>
            {dp && <span className="badge bg-light text-dark">#{dp.id}</span>}
          </div>

          {dp ? (
            <>
              <div className="row g-3 mb-1">
                <div className="col-md-3"><b>Amount</b><div>{formatIDR(dp.amount)}</div></div>
                <div className="col-md-3"><b>Status</b><div><StatusBadge status={dp.status} /></div></div>
                <div className="col-md-3">
                  <b>Method</b>
                  <select
                    className="form-select mt-1"
                    value={dp.payment_method || ''}
                    onChange={e => changeMethod(dp, e.target.value)}
                    disabled={busy}
                  >
                    {METHOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="col-md-3"><b>Date</b><div>{new Date(dp.created_at).toLocaleString('id-ID')}</div></div>
              </div>

              <div className="mt-3 btn-group">
                {target === 'dp' && dp && (
                  <button
                    className="btn btn-outline-success"
                    disabled={busy}
                    onClick={() => approve(dp, 'complete_dp')}
                  >
                    Approve DP
                  </button>
                )}

                {target === 'full' && full && (
                  <button
                    className="btn btn-outline-success"
                    disabled={busy || isFinalPaid}
                    onClick={onApproveFullClick} // pakai handler yang sudah ada utk validasi Final Price
                  >
                    Approve Full
                  </button>
                )}
                <button className="btn btn-outline-secondary" disabled={busy} onClick={()=>approve(dp,'canceled')}>Cancel</button>
                <button className="btn btn-outline-danger"  disabled={busy} onClick={()=>approve(dp,'failed')}>Mark Fake / Reject</button>
              </div>
            </>
          ) : (
            <div className="text-muted">No DP payment created yet.</div>
          )}
        </div>
      </div>

      {/* FULL card */}
      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Full Payment</h5>
            {full && <span className="badge bg-light text-dark">#{full.id}</span>}
          </div>

          {full ? (
            <>
              {/* ...existing FULL content... */}
            </>
          ) : (
            <>
              <div className="alert alert-info d-flex align-items-center small mb-2" role="alert">
                <span className="me-2" aria-hidden>ℹ️</span>
                <div>
                  Belum ada <b>FULL</b> payment untuk order ini.
                  Placeholder <b>FULL</b> dibuat otomatis saat <b>Order</b> di-<i>accept</i>.
                  Jika Order sudah di-accept namun belum muncul, coba <i>refresh</i> halaman atau hubungi Finance.
                </div>
              </div>
              <div className="text-muted">No Full payment created yet.</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
